import { Box, Button, Chip, CircularProgress, Divider, Link, Typography } from '@mui/material';
import { OpenInNew as OpenIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../erp/formatDate';
import { BACKGROUND_CHECK_POINTS, useCheckPoints } from './backgroundCheckPricing';

/**
 * Antecedentes del postulante en el FORMATO COMÚN que entrega el backend
 * (utils/backgroundCheck: Chapi, Alto o el legado de Alto — ver
 * PLAN_MIGRACION_CHAPI). Un solo componente para cualquier proveedor:
 * identidad, score, un renglón por reporte con el color del proveedor,
 * causas curadas (solo en los reportes con alertas) y link al informe.
 * Sin alertas no se despliega nada más: la ausencia de alertas ES el estado
 * limpio. Si la consulta terminó en error, `canRetry` (lo calcula el backend:
 * RUT chileno válido) habilita el botón de reintento (fase 3).
 */

const PROVIDER_NAME = { chapi: 'Chapi', alto: 'Alto Checks' };

// Colores de los badges del proveedor → paleta del drawer
const TONES = {
  green: { color: '#166534', bg: '#DCFCE7' },
  yellow: { color: '#92400E', bg: '#FEF3C7' },
  orange: { color: '#9A3412', bg: '#FFEDD5' },
  red: { color: '#991B1B', bg: '#FEE2E2' },
  gray: { color: '#374151', bg: '#F3F4F6' },
};
const SCORE_TONE = { low: 'green', medium: 'yellow', high: 'red' };
const IDENTITY_TONE = { partial: 'yellow', mismatch: 'red', not_found: 'red' };

// Orden en que se muestran los campos de una causa (los que no vienen se omiten)
const CAUSA_FIELDS = [
  'caratulado', 'rit', 'ruc', 'tribunal', 'fechaIngreso', 'rol', 'formaInicio',
  'estado', 'etapa', 'estadoProcesal', 'procedimiento', 'materia', 'delitos', 'matchType',
];
// Fechas hacia el usuario siempre DD/MM/AAAA (CLAUDE.md → Fechas)
const DATE_FIELDS = new Set(['fechaIngreso']);

const chipSx = (tone) => ({
  backgroundColor: tone.bg, color: tone.color, fontWeight: 700, height: 20, fontSize: 11,
});

const hasValue = (v) => v !== null && v !== undefined && typeof v !== 'object' && String(v).trim() !== '';

const ReportRow = ({ report, t }) => {
  const tone = TONES[report.color] || TONES[report.hasFlags ? 'yellow' : 'gray'];
  const causas = Array.isArray(report.causas) ? report.causas : [];
  const soloPorNombre = report.type === 'penal' && report.rutMatchCount === 0 && report.nameMatchCount > 0;
  const badge = report.processing
    ? t('ats.backgroundCheck.processing')
    : t(report.hasFlags ? 'ats.backgroundCheck.flagged' : 'ats.backgroundCheck.clean');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" fontWeight={600}>
          {t(`ats.backgroundCheck.reportType.${report.type}`, { defaultValue: report.label || report.type })}
        </Typography>
        <Chip size="small" label={badge} sx={chipSx(report.processing ? TONES.gray : tone)} />
        {report.hasFlags && typeof report.causasCount === 'number' && report.causasCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('ats.backgroundCheck.causasCount', { count: report.causasCount })}
          </Typography>
        )}
      </Box>
      {soloPorNombre && (
        <Typography variant="caption" sx={{ display: 'block', color: TONES.yellow.color }}>
          {t('ats.backgroundCheck.nameMatchOnly')}
        </Typography>
      )}
      {report.hasFlags && causas.length > 0 && (
        <Box sx={{ mt: 0.5, p: 1.25, backgroundColor: tone.bg, borderRadius: 1, maxHeight: 300, overflowY: 'auto' }}>
          {causas.map((causa, ci) => (
            <Box key={ci} sx={{ mb: 0.75, pl: 1, borderLeft: `2px solid ${tone.color}` }}>
              {CAUSA_FIELDS.filter((k) => hasValue(causa[k])).map((k) => (
                <Typography key={k} variant="caption" sx={{ display: 'block', color: tone.color }}>
                  <strong>{t(`ats.backgroundCheck.fields.${k}`, { defaultValue: k })}</strong>:{' '}
                  {k === 'matchType'
                    ? t(`ats.backgroundCheck.matchType.${causa[k]}`, { defaultValue: String(causa[k]) })
                    : (DATE_FIELDS.has(k) ? formatDate(causa[k]) : String(causa[k]))}
                </Typography>
              ))}
            </Box>
          ))}
          {report.truncated && (
            <Typography variant="caption" sx={{ display: 'block', color: tone.color, fontStyle: 'italic' }}>
              {t('ats.backgroundCheck.truncated', { shown: causas.length, total: report.causasCount ?? causas.length })}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// retryPoints: precio del reintento en puntos (2026-11-01, PLAN_COBRO_ANTECEDENTES)
const AtsBackgroundCheckSection = ({ check, canRetry = false, retrying = false, onRetry = null, retryPoints = BACKGROUND_CHECK_POINTS }) => {
  const { t } = useTranslation();
  const { points: retryPointsText } = useCheckPoints(retryPoints);
  if (!check) return null;

  const provider = PROVIDER_NAME[check.provider] || check.provider || '';
  const identity = check.identity || null;
  const identityTone = identity && TONES[IDENTITY_TONE[identity.level]];
  const reports = Array.isArray(check.reports) ? check.reports : [];
  const hasFlags = reports.some((r) => r && r.hasFlags);
  const officialLabel = check.provider === 'alto'
    ? t('ats.backgroundCheck.officialNameRegistry')
    : t('ats.backgroundCheck.officialName', { provider });
  const scoreTone = check.score && TONES[SCORE_TONE[check.score.label]];
  const reportUrl = check.pdfUrl || check.permalink || null;

  return (
    <>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t('ats.backgroundCheck.title', { provider })}
          </Typography>
          {check.status === 'finished' && scoreTone && (
            <Chip
              size="small"
              label={t('ats.backgroundCheck.score', {
                label: t(`ats.backgroundCheck.scoreLabel.${check.score.label}`, { defaultValue: check.score.label }),
                value: check.score.value ?? '—',
              })}
              sx={chipSx(scoreTone)}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Identidad — match: línea discreta; el color de caja queda para lo que requiere atención */}
          {identity && identity.level === 'match' && (
            <Typography variant="caption" sx={{ color: '#16A34A', fontWeight: 600 }}>
              {t('ats.backgroundCheck.identity.match')}
              {check.officialName ? ` — ${check.officialName}` : ''}
            </Typography>
          )}
          {identity && identityTone && (
            <Box sx={{ p: 1, backgroundColor: identityTone.bg, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: identityTone.color, fontWeight: 700, display: 'block' }}>
                {t(`ats.backgroundCheck.identity.${identity.level}`)}
              </Typography>
              {identity.detail && (
                <Typography variant="caption" sx={{ color: identityTone.color, display: 'block' }}>
                  {identity.detail}
                </Typography>
              )}
              {check.officialName && identity.level !== 'not_found' && (
                <Typography variant="caption" sx={{ color: identityTone.color, display: 'block' }}>
                  {officialLabel}: {check.officialName}
                </Typography>
              )}
            </Box>
          )}

          {check.status === 'processing' && (
            <Chip
              size="small"
              label={`${t('ats.backgroundCheck.processing')}${check.alto && typeof check.alto.progress === 'number' ? ` (${Math.round(check.alto.progress)}%)` : ''}`}
              sx={{ alignSelf: 'flex-start', backgroundColor: '#EDE9FE', color: '#4C1D95', fontWeight: 600 }}
            />
          )}

          {check.status === 'error' && (
            <Box sx={{ p: 1, backgroundColor: TONES.gray.bg, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: TONES.gray.color, fontWeight: 700, display: 'block' }}>
                {t('ats.backgroundCheck.error.title')}
              </Typography>
              <Typography variant="caption" sx={{ color: TONES.gray.color, display: 'block' }}>
                {t(`ats.backgroundCheck.error.${check.error}`, { defaultValue: t('ats.backgroundCheck.error.provider_error') })}
              </Typography>
              {canRetry && onRetry && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onRetry}
                    disabled={retrying}
                    startIcon={retrying ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('ats.backgroundCheck.retry')}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {t('ats.backgroundCheck.retryHint', { points: retryPointsText })}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {check.status !== 'error' && reports.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {reports.map((report, i) => (
                <ReportRow key={`${report.type || 'reporte'}-${i}`} report={report} t={t} />
              ))}
            </Box>
          )}

          {/* La advertencia de homonimia solo importa cuando HAY alertas */}
          {hasFlags && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('ats.backgroundCheck.homonymDisclaimer')}
            </Typography>
          )}

          {reportUrl && (
            <Link
              href={reportUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, alignSelf: 'flex-start' }}
            >
              {t('ats.backgroundCheck.openReport', { provider })}
              <OpenIcon sx={{ fontSize: 14 }} />
            </Link>
          )}
        </Box>
      </Box>
    </>
  );
};

export default AtsBackgroundCheckSection;
